import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import countryList from "react-select-country-list";
import { APPINSIGHTS_STRINGS } from "../../constants/appInsightsStrings";
import { trackEvent } from "../../utils/appInsights";

import { savePlant, editPlant, getPlantDetails } from "../../network/cipAPI";
import { storeUnits } from "../../Store/TemplateStore/template.action";

import Header from "../../components/Header";
import ErrorLabel from "../../components/ErrorLabel";
import InputBox from "../../components/InputBox";
import Dropdown from "../../components/Dropdown";
import DupontButton from "../../components/DupontButton";
import PageLoading from "../../components/PageLoading";
import createNotification, {
  SUCCESS_NOTIFICATION,
  ERROR_NOTIFICATION,
} from "../../components/Notifications";
import Settings from "../home/Settings";

import {
  getChangedData,
  extractPlantId,
  updatePlantId,
} from "../../utils/Utils";
import { saveItemSession } from "../../utils/session";
import { PLANT_DETAILS } from "./addNewPlant.config";
import { WATER_TYPE } from "./addNewPlant.config";
import RoConfiguration from "./roConfiguration";
import { API_URLS } from "../../network/appUrls";
import "./index.scss";
import Footer from "../../common/footer";
import { MODULE_BRAND_KEYS } from "../../constants/globalStrings";

const basePath = API_URLS.basePath;

const AddNewPlant = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const plantDetails = PLANT_DETAILS(t);
  let countryOptions = useMemo(() => countryList().getData(), []);
  countryOptions = countryOptions.map((item) => item.label);
  const [waterTypeOptions, setWaterTypeOptions] = useState(WATER_TYPE(t));

  const searchParams = new URLSearchParams(window.location.search);
  const isEdit = searchParams.get("isEdit");

  const [plantDetail, setPlantDetail] = useState([]);
  const [plantDetailerror, setPlantDetailError] = useState([]);
  const [country, setCountry] = useState("");
  const [waterType, setWaterType] = useState("");
  const [trains, setTrains] = useState([]);
  const [totalNumberOfElements, setTotalNumberOfElements] = useState("");
  const [totalNumberOfActiveArea, setTotalNumberOfActiveArea] = useState("");
  const [stageValidationSucessfull, setStageValidationSucessfull] =
    useState(true);
  const [fetchedPlanDetails, setFetchedPlanDetails] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [moduleBrand, setModuleBrand] = useState(MODULE_BRAND_KEYS[0]);
  const {
    flow,
    pressure,
    feed_suspended,
    filtrate_suspended,
    temperature,
    flux,
    volume,
    mass,
    currency,
    conversion_rate,
    userEmail,
    token,
  } = useSelector((state) => state);
  const plantId = extractPlantId();

  const moduleBrandOptions = MODULE_BRAND_KEYS.map((key) =>
    t(`addNewPlant.${key}`)
  );
  // Get key from label (for dropdown selection)
  const getModuleBrandKey = (label) =>
    MODULE_BRAND_KEYS.find((key) => t(`addNewPlant.${key}`) === label);
  // Get label from key (for display)
  const getModuleBrandLabel = (key) => {
    if (!key) return "";
    return t(`addNewPlant.${key}`);
  };

  useEffect(() => {
    setWaterTypeOptions(WATER_TYPE(t));
  }, [t]);

  useEffect(() => {
    if (token && isEdit && plantId) {
      const getPlantDetail = async () => {
        setIsLoading(true);
        try {
          const response = await getPlantDetails();
          if (response && response.data) {
            const detail = response.data;
            const {
              name,
              description,
              city,
              country,
              water_type,
              module_brand,
              config,
              setting,
            } = detail;

            setFetchedPlanDetails(detail);
            setPlantDetail({
              ...plantDetail,
              installationName: name,
              description,
              city,
            });
            setCountry(country);
            setWaterType(water_type);
            if (module_brand) {
              setModuleBrand(module_brand);
            }
            setTrains(config?.trains);
            dispatch(storeUnits(setting));
          }
        } catch (error) {
          console.log("getPlantDetail", error);
          setIsLoading(false);
        } finally {
          setIsLoading(false);
        }
      };
      getPlantDetail();
    }
  }, [token]);

  const changeCountry = (value) => {
    setCountry(value);
    setPlantDetailError({ ...plantDetailerror, ["country"]: "" });
  };
  const changeWaterType = (value) => {
    setWaterType(value);
    setPlantDetailError({ ...plantDetailerror, ["waterType"]: "" });
  };
  const changeModuleBrand = (value) => {
    setModuleBrand(getModuleBrandKey(value));
    setPlantDetailError({ ...plantDetailerror, ["moduleBrand"]: "" });
  };
  const handleInputChange = (key, value) => {
    setPlantDetail({ ...plantDetail, [key]: value });
    setPlantDetailError({ ...plantDetailerror, [key]: "" });
  };
  const runValidation = () => {
    setStageValidationSucessfull(false);
    let validationSuccessful = true;
    const error = [];
    if (!plantDetail.installationName) {
      validationSuccessful = false;
      error["installationName"] = "addNewPlant.installationNameRequired";
    }
    if (!country) {
      validationSuccessful = false;
      error["country"] = "addNewPlant.countryRequired";
    }
    if (!waterType) {
      validationSuccessful = false;
      error["waterType"] = "addNewPlant.waterTypeRequired";
    }
    if (!moduleBrand) {
      validationSuccessful = false;
      error["moduleBrand"] = "addNewPlant.moduleBrandRequired";
    }
    setPlantDetailError({
      ...plantDetailerror,
      ...error,
    });
    for (const train of Object.values(trains)) {
      if (train?.racks?.length === 0) {
        validationSuccessful = false;
        break;
      }

      for (const rack of train.racks) {
        if (
          rack.num_modules_per_rack === undefined ||
          // rack.modules_used === undefined ||
          rack.active_area === undefined
        ) {
          validationSuccessful = false;
          break;
        }
      }
    }
    return validationSuccessful;
  };
  const normalizedFetchedRate = parseFloat(
    fetchedPlanDetails?.setting?.conversion_rate ?? ""
  );
  const normalizedCurrentRate = parseFloat(conversion_rate ?? "");
  const isConversionRateChanged =
    normalizedFetchedRate !== normalizedCurrentRate;
  const savePlantDetails = async (key) => {
    setIsLoading(true);
    try {
      const validated = runValidation();
      let response = null;
      if (validated) {
        const payload = {
          name: plantDetail.installationName,
          description: plantDetail.description,
          city: plantDetail.city,
          country: country,
          water_type: waterType,
          module_brand: moduleBrand,
          setting: {
            flow: flow,
            pressure: pressure,
            feed_suspended: feed_suspended,
            filtrate_suspended: filtrate_suspended,
            temperature: temperature,
            flux: flux,
            volume: volume,
            mass: mass,
            currency: currency,
            ...(isConversionRateChanged && {
              conversion_rate: conversion_rate,
            }),
          },
          config: {
            trains: [...Object.values(trains)],
          },
          tol_num_modules: totalNumberOfElements,
          tol_active_area: totalNumberOfActiveArea,
        };

        saveItemSession("water_type", waterType);
        saveItemSession("name", plantDetail.installationName);
        if (plantId && isEdit) {
          const editedFields = getChangedData(payload, fetchedPlanDetails);
          response = await editPlant(editedFields);
        } else {
          response = await savePlant(payload);
        }
        if (response.created_at) {
          // Track successful plant creation/edit
          trackEvent(
            isEdit
              ? APPINSIGHTS_STRINGS.PLANT_EDITED
              : APPINSIGHTS_STRINGS.PLANT_ADDED,
            {
              userEmail: userEmail,
              plantName: plantDetail.installationName,
              country: country,
              waterType: waterType,
              totalElements: totalNumberOfElements,
              s: totalNumberOfActiveArea,
            }
          );

          if (key === "exit") {
            navigate(-1);
            const message = isEdit
              ? t("addNewPlant.successfullyEdited")
              : t("addNewPlant.successfullyAdded");
            createNotification(SUCCESS_NOTIFICATION, message, "");
          } else {
            updatePlantId(response.plant_id);
            let path = "";
            if (isEdit)
              path = `${basePath}analyseData?plantId=${response.plant_id}&isEdit=${isEdit}`;
            else path = `${basePath}analyseData?plantId=${response.plant_id}`;
            navigate(path);
          }
        } else {
          createNotification(ERROR_NOTIFICATION, response.detail.error, "");
        }
      } else {
        setIsLoading(false);
      }
    } catch (error) {
      console.log("savePlantDetails", error);
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="container-main">
      <Header />
      <div className="main-container">
        <div className="content-container descriptionText">
          {isLoading ? (
            <PageLoading customHeight={"515px"} />
          ) : (
            <div className="calculatedValueText">
              {isEdit
                ? t("roAdvisorProject.editPlant")
                : t("roAdvisorProject.addPlant")}
              {Object.values(plantDetails).map((field, index) => (
                <div key={index}>
                  <InputBox
                    name={field.key}
                    value={plantDetail[field.key]}
                    labelText={t(`addNewPlant.${field.key}`)}
                    placeholder={t(`addNewPlant.${field.key}`)}
                    rows={field.multiline}
                    isRequired={field.required}
                    onChange={(value) => handleInputChange(field.key, value)}
                    errorMsg={
                      plantDetailerror[field.key]
                        ? t(plantDetailerror[field.key])
                        : ""
                    }
                    maxLength={field.maxChar}
                  />
                </div>
              ))}
              <div className="marginTop5">
                <Dropdown
                  labelText={t("addNewPlant.country")}
                  placeholder={t("addNewPlant.country")}
                  value={country}
                  onChange={changeCountry}
                  options={countryOptions}
                  errorMsg={
                    plantDetailerror["country"]
                      ? t(plantDetailerror["country"])
                      : ""
                  }
                  isRequired
                />
              </div>
              <div className="marginTop5 flex-gap-20">
                <div className="flex_1">
                  <Dropdown
                    labelText={t("addNewPlant.waterType")}
                    placeholder={t("addNewPlant.waterType")}
                    value={waterType}
                    onChange={changeWaterType}
                    options={waterTypeOptions}
                    errorMsg={
                      plantDetailerror["waterType"]
                        ? t(plantDetailerror["waterType"])
                        : ""
                    }
                    isRequired
                  />
                </div>
                <div className="flex_1">
                  <Dropdown
                    labelText={t("addNewPlant.moduleBrand")}
                    placeholder={t("addNewPlant.moduleBrand")}
                    value={getModuleBrandLabel(moduleBrand)}
                    onChange={changeModuleBrand}
                    options={moduleBrandOptions}
                    isRequired
                    errorMsg={
                      plantDetailerror["moduleBrand"]
                        ? t(plantDetailerror["moduleBrand"])
                        : ""
                    }
                  />
                </div>
              </div>
              <div>
                <div className="bodyMainHeadingText margin-top-20 flex-ro font-bold">
                  {t("addNewPlant.reverseOsmosis")}
                  {!stageValidationSucessfull && (
                    <ErrorLabel
                      className="margin-left-ro"
                      message={t("addNewPlant.mandatoryRequired")}
                    />
                  )}
                </div>
                <div className="reverse-osmosis description-text ">
                  <RoConfiguration
                    trains={trains}
                    setTrains={setTrains}
                    setTotalNumberOfElements={setTotalNumberOfElements}
                    setTotalNumberOfActiveArea={setTotalNumberOfActiveArea}
                    rackValidationSucessfull={stageValidationSucessfull}
                    setStageValidationSucessfull={setStageValidationSucessfull}
                  />

                  <div className="flex-between padding-top-20">
                    <div className="flex-between-center">
                      {t("addNewPlant.totalNoOfActiveArea")}
                      <div className="padding-left-50">
                        <InputBox
                          value={totalNumberOfActiveArea}
                          placeholder={t(`addNewPlant.totalNoOfActiveArea`)}
                          disabled
                          hideClearBtn
                        />
                      </div>
                    </div>
                    <div className="flex-between-center">
                      {t("addNewPlant.totalNoOfElements")}
                      <div className="padding-left-50">
                        <InputBox
                          value={totalNumberOfElements}
                          placeholder={t(`addNewPlant.totalNoOfElements`)}
                          disabled
                          hideClearBtn
                        />
                      </div>
                    </div>
                  </div>
                  <div clasName="relative-container">
                    <div className="absolute-element"></div>
                    <Settings
                      disabled={false}
                      descriptionText={t(
                        "addNewPlant.unitOfMeasureDescription"
                      )}
                    />
                  </div>
                </div>
                <div className="flex-jc-end padding-top-20 ">
                  <DupontButton
                    customClass={"margin-bottom margin-left-10"}
                    onClick={() => savePlantDetails("exit")}
                    disabled={!stageValidationSucessfull}
                  >
                    {t("ValueStrings.saveExit")}
                  </DupontButton>
                  <DupontButton
                    customClass={"margin-bottom margin-left-10"}
                    onClick={() => savePlantDetails("continue")}
                    disabled={!stageValidationSucessfull}
                  >
                    {t("ValueStrings.saveContinue")}
                  </DupontButton>
                </div>
              </div>
            </div>
          )}
        </div>
        <Footer />
      </div>
    </div>
  );
};
export default AddNewPlant;
